/**
 * THE APPROVED CTA LIST — port of the monolith's `CTA_25` / `ECH_CHOICES` /
 * `__echChoiceFor` / `__echChoiceHtml` (index.html L11096–11144), audit row 15.
 *
 * WHY IT EXISTS (the monolith's own note, kept because the reasoning is the spec)
 * ------------------------------------------------------------------------------
 * Button labels were free text, which is how a 200px button ends up with copy that
 * can't fit and how the same action gets five different names across 47 divisions.
 * These are the 25 real labels from CTA-Top25-For-Creative-Approval, grouped by
 * intent.
 *
 * ONE OF THE 25 BREAKS THE RULE: "Contact Lennar Mortgage" is 23 characters
 * against a 20-character cap on a button locked at 200px. It is a real approved
 * label and it genuinely doesn't fit, so it is offered but DISABLED with the count
 * shown — the answer is Layne shortening it (e.g. "Lennar Mortgage"), not the
 * Studio pretending either the list or the cap is wrong.
 *
 * A value already on the block that isn't in the list is KEPT and shown under "On
 * this block" — this never silently rewrites someone's copy.
 *
 * THE OPTIONS ARE DATA, NOT MARKUP. The monolith builds an HTML string because it
 * renders the panel with innerHTML; React needs the same decisions as a structure,
 * so `echChoiceGroups()` returns the optgroups and this file renders nothing.
 */

import { echByName } from "./echRegistry";
import { echList } from "./echLists";

/** Layne's approved-label list block — the source for every CTA dropdown when loaded */
export const CTA_LIST_BLOCK = "ECH_LIST_CTALabels";

/** the 25 approved labels, grouped by intent (monolith L11110–11117) — FALLBACK ONLY, see echChoiceGroups */
export const CTA_25: [string, string[]][] = [
  /* "View Floorplans" added 2026-10-06 after "View Floorplan", as Layne added it to
     ECH_LIST_CTALabels for the Just Released - Floorplans template */
  ["Browse inventory", ["View Homes", "View Home", "View All", "View Floorplan", "View Floorplans", "View Community", "View Communities"]],
  ["Tour & visit", ["Book a Tour", "Schedule a Tour", "Book an Appointment", "Take a 360° Tour", "Get Directions"]],
  /* "Call Now" added 2026-09-22 (pilot #128, Kailey White NEV) — resolves to `tel:` + the
     header's phone in ctaUrlFor. A field SFMC has converted (`optionsFrom`) reads its list
     from the ECH_LIST_* block instead, so Layne adds it there too. */
  ["Talk to someone", ["Contact Us", "Contact a Consultant", "Contact Lennar Mortgage", "Request Info", "Call Now"]],
  ["Offer & incentive", ["View Offer", "Get an Offer", "Get Pre-Qualified"]],
  ["Event", ["RSVP", "Add to Calendar", "Join VIP List", "Join Interest List", "Watch Now"]],
  ["General", ["Learn More", "Refer a Friend"]],
];

/**
 * Which fields get the list, per block (monolith `ECH_CHOICES` L11119–11125).
 *
 * ⚠️ EVERY KEY HERE IS READ OFF THE BLOCK'S OWN `@GUARDRAILS`, NOT OFF ITS SIBLING'S
 * (re-verified against the block cache pulled 2026-08-18, corrected 2026-08-20).
 * Four of the seven CTA-bearing blocks were listed under keys they do not declare,
 * and the two Featured Plan blocks were absent altogether:
 *
 *   block              was              actually declares
 *   ECH_GOTY1CTA       btnLabel         CtaLabel
 *   ECH_GOInvite1CTA   btnLabel         CtaLabel
 *   ECH_GOInvite2CTA   btn1/btn2Label   cta1Label / cta2Label
 *   ECH_Featured1CTA   —                BtnLabel   (capital B)
 *   ECH_Featured2CTA   —                btn1Label / btn2Label
 *
 * A key that misses does not fail loudly — it fails as an ABSENCE, twice over:
 *   1. the panel falls back to a plain text box, so the requester types the button
 *      copy free-hand. That is the precise thing the 25-label list exists to stop
 *      (see the header above): copy that cannot fit a 200px button, and five names
 *      for one action across 47 divisions.
 *   2. `echInline.inlineTargets` excludes a field by asking `echChoiceFor()`
 *      (echInline.ts:161), so the label ALSO stayed double-click editable on the
 *      canvas — a second way round the same list. Jack, 2026-08-19: "should not be
 *      able to update the text for the buttons though."
 *
 * `ECH_CTA_PAIRS` in echDyn.ts had the same keys right all along. The two maps are
 * now cross-checked against each other and against the fixture's real guardrails in
 * echChoice.test.ts, so a new CTA block cannot land in one map and not the other.
 */
export const ECH_CHOICES: Record<string, Record<string, string>> = {
  ECH_2CTA: { btn1Label: "cta", btn2Label: "cta" },
  ECH_1CTA: { btnLabel: "cta" },
  ECH_GOTY1CTA: { CtaLabel: "cta" },
  ECH_GOTY2CTA: { cta1Label: "cta", cta2Label: "cta" },
  ECH_GOInvite2CTA: { cta1Label: "cta", cta2Label: "cta" },
  ECH_GOInvite1CTA: { CtaLabel: "cta" },
  /* the Featured Plan blocks carry governed buttons too — 200px, same cap, same
     approved list. Their unit labels (SqFtLabel / BedsLabel / BathsLabel) are NOT
     buttons and stay free text on purpose: the block's own note requires "1 bed" to
     be made singular. */
  ECH_Featured1CTA: { BtnLabel: "cta" },
  ECH_Featured2CTA: { btn1Label: "cta", btn2Label: "cta" },
};

/**
 * "cta" when this block's field is an approved-label field, "" otherwise.
 *
 * ── THE MIGRATION SEAM (2026-08-27) ──────────────────────────────────────────
 * A field that DECLARES `optionsFrom` answers for itself, and `ECH_CHOICES` is not
 * consulted at all. That is the end state Layne's spec describes: the field carries
 * its own reference and this hand-maintained table stops existing.
 *
 * Until every CTA field is converted in SFMC, both paths run side by side, and that
 * is deliberate rather than lazy — it means this can ship BEFORE the block and the
 * ten field changes are published without changing what an author sees. A converted
 * field reads its vocabulary from SFMC; an unconverted one keeps rendering today's
 * list. No flag day, and no window where the dropdown is empty.
 *
 * `ECH_CHOICES` and `CTA_25` get deleted once the live blocks all declare
 * `optionsFrom` — that cleanup is the follow-up, not this change.
 */
export function echChoiceFor(name: string, k: string): string {
  if (echFieldList(name, k)) return "cta";
  return (ECH_CHOICES[name] || {})[k] || "";
}

/** the `ECH_LIST_*` block a field declares, or "" — the field's own reference */
export function echFieldList(name: string, k: string): string {
  const meta = echByName(name);
  const dec = meta && meta.declared && meta.declared[k];
  return (dec && String(dec.optionsFrom || "")) || "";
}

/** true when the field forbids a custom value (declared on the field or the list) */
export function echChoiceStrict(name: string, k: string): boolean {
  const meta = echByName(name);
  const dec = meta && meta.declared && meta.declared[k];
  if (dec && dec.strict === true) return true;
  const l = echList(echFieldList(name, k));
  return !!l && l.strict;
}

export interface ChoiceOpt {
  value: string;
  /** what the option READS — the label, plus "— too long (n/cap)" when over */
  text: string;
  /** an approved label that can't fit this block's button */
  disabled: boolean;
}
export interface ChoiceGroup { label: string; opts: ChoiceOpt[] }

/**
 * The optgroups for one CTA field.
 *
 * `cap` is the block's own guardrail cap for the field (0 = no cap declared, in
 * which case nothing is ever disabled). The CURRENT value is never disabled even
 * when it is over — disabling the selected option makes the select render blank.
 */
export function echChoiceGroups(value: unknown, cap?: number, listName?: string): ChoiceGroup[] {
  const cur = String(value == null ? "" : value);
  const c = cap || 0;
  let seen = false;
  /* SOURCE OF TRUTH: the `ECH_LIST_*` block when the field names one, this repo's
     table only for fields SFMC has not converted yet. Declared order is preserved on
     both paths — the list is editorial, not alphabetical. */
  /* ── THE BLOCK IS THE SOURCE FOR EVERY BUTTON, NAMED OR NOT (2026-10-06, Layne: "I thought
     the button list was read from ECH_LIST_CTALabels… I'd like it removed so the block stays
     the only source") ──────────────────────────────────────────────────────────────────────
     A field that declares no `optionsFrom` used to read `CTA_25`, this repo's copy — so
     "View Floorplans" had to be added in two places. It now reads `ECH_LIST_CTALabels`
     whenever that block is loaded (it also rides the localStorage mirror, so it is there on a
     cold open). `CTA_25` is left only as the answer when the list block cannot be read at
     all, and goes away with `ECH_CHOICES` once every CTA field declares `optionsFrom`. */
  const listed = (n: string) => (echList(n)?.groups || []).map((g) => [g.group, g.options] as [string, string[]]);
  const src: [string, string[]][] = listName
    ? listed(listName)
    : (echList(CTA_LIST_BLOCK) ? listed(CTA_LIST_BLOCK) : CTA_25);
  const groups: ChoiceGroup[] = src.map(([label, opts]) => ({
    label,
    opts: opts.map((o) => {
      if (o === cur) seen = true;
      /* an approved label that can't fit this block's button is shown but not
         selectable — hiding it would look like the list is wrong */
      const over = !!(c && o.length > c && o !== cur);
      return { value: o, text: o + (over ? " — too long (" + o.length + "/" + c + ")" : ""), disabled: over };
    }),
  }));
  /* keep whatever is already on the block, even off-list */
  if (cur && !seen) groups.unshift({ label: "On this block", opts: [{ value: cur, text: cur, disabled: false }] });
  return groups;
}

/** the monolith's `<option value="" selected disabled>` placeholder — only with no value */
export const CHOICE_PLACEHOLDER = "Choose a button label…";
